/**
 * PdfPreviewHost — global einmal gemountet (in AppShell). Zeigt PDFs in einem
 * Vollbild-Overlay mit eingebettetem Viewer (iframe) + „Senden" / „Drucken" /
 * „Herunterladen". Wird über das Singleton in `lib/pdfPreview` angesteuert.
 *
 * Jun-2026 (User-Wunsch) — „Senden"-Panel: jedes PDF kann direkt per E-Mail
 * (Anhang), WhatsApp (wa.me-Link) oder SMS (Link) an einen Kunden verschickt
 * werden. Empfänger wird aus dem Dokument-Kontext vorbefüllt (editierbar).
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { Download, Printer, X, FileText, Send, Mail, MessageCircle, Smartphone, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { registerPdfPreview } from "../lib/pdfPreview";
import api, { formatApiErrorDetail } from "../lib/api";
import PdfCanvasViewer from "./PdfCanvasViewer";

const CHANNELS = [
    { key: "email", label: "E-Mail", icon: Mail },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
    { key: "sms", label: "SMS", icon: Smartphone },
];

/** Standard-Begleittext je nach Dokumenttyp (aus Dateiname/docType abgeleitet).
 *  Spart dem Händler Tipparbeit und sorgt für einen einheitlichen Ton. */
function defaultMessageFor(filename = "", docType = "") {
    const s = `${docType} ${filename}`.toLowerCase();
    const greet = "Guten Tag,";
    let core;
    if (s.includes("mahnung")) core = "anbei erhalten Sie unsere Zahlungserinnerung. Bitte begleichen Sie den offenen Betrag zeitnah.";
    else if (s.includes("rechnung")) core = "anbei erhalten Sie Ihre Rechnung. Vielen Dank für Ihren Auftrag.";
    else if (s.includes("kostenvoranschlag") || s.includes("angebot")) core = "anbei erhalten Sie Ihren Kostenvoranschlag. Wir freuen uns auf Ihre Rückmeldung.";
    else if (s.includes("kaufvertrag") || s.includes("vertrag")) core = "anbei erhalten Sie Ihren Kaufvertrag. Bitte prüfen Sie die Angaben und melden Sie sich bei Fragen.";
    else if (s.includes("probefahrt")) core = "anbei erhalten Sie Ihre Probefahrt-Vereinbarung. Bitte bringen Sie diese unterschrieben sowie Führerschein und Ausweis mit.";
    else if (s.includes("bestellung") || s.includes("bestell")) core = "anbei erhalten Sie Ihre Bestellung.";
    else if (s.includes("auftrag")) core = "anbei erhalten Sie Ihren Auftrag.";
    else if (s.includes("beleg")) core = "anbei erhalten Sie Ihren Zahlungsbeleg.";
    else if (s.includes("protokoll")) core = "anbei erhalten Sie das zugehörige Protokoll.";
    else core = "anbei erhalten Sie Ihr Dokument.";
    // Aug-2026 (User-Wunsch): ohne Grußformel — die E-Mail hängt sie mit
    // Firmenname automatisch an (sonst stand sie doppelt im Text).
    return `${greet} ${core}`;
}

export default function PdfPreviewHost() {
    const [state, setState] = useState(null); // { url, filename, blob, recipient }
    const urlRef = useRef(null);
    const blobRef = useRef(null);

    // Senden-Panel
    const [sendOpen, setSendOpen] = useState(false);
    const [channel, setChannel] = useState("email");
    const [toValue, setToValue] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);

    const close = useCallback(() => {
        if (urlRef.current) { window.URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
        blobRef.current = null;
        setSendOpen(false);
        setState(null);
    }, []);

    useEffect(() => {
        registerPdfPreview((blob, filename, opts = {}) => {
            if (urlRef.current) window.URL.revokeObjectURL(urlRef.current);
            const url = window.URL.createObjectURL(blob);
            urlRef.current = url;
            blobRef.current = blob;
            const recipient = opts.recipient || {};
            setState({ url, filename, recipient, blob });
            // Standard-Kanal je nach vorhandenen Kontaktdaten
            const initChannel = recipient.email ? "email" : (recipient.phone ? "whatsapp" : "email");
            setChannel(initChannel);
            setToValue(initChannel === "email" ? (recipient.email || "") : (recipient.phone || ""));
            setMessage(defaultMessageFor(filename, opts.docType));
            setSendOpen(false);
            // Jun-2026 (Bugfix) — Manche Komponenten (Radix-Dialog) hinterlassen beim
            // gleichzeitigen Schließen ein `pointer-events: none` am <body>, wodurch die
            // Buttons dieser Vorschau (Drucken/Herunterladen) nicht klickbar wären.
            // Wir heben die Sperre beim Öffnen der Vorschau zuverlässig wieder auf.
            const unlock = () => { try { document.body.style.pointerEvents = ""; } catch { /* noop */ } };
            unlock();
            [50, 200, 500].forEach((ms) => setTimeout(unlock, ms));
            // Optional: Druckdialog direkt auslösen (opts.autoPrint) — Vorschau bleibt im Hintergrund.
            if (opts.autoPrint) {
                const purl = url;
                setTimeout(() => {
                    try {
                        const f = document.createElement("iframe");
                        f.style.position = "fixed"; f.style.right = "0"; f.style.bottom = "0";
                        f.style.width = "0"; f.style.height = "0"; f.style.border = "0";
                        f.src = purl;
                        f.onload = () => {
                            try { f.contentWindow.focus(); f.contentWindow.print(); }
                            catch { window.open(purl, "_blank", "noopener"); }
                        };
                        document.body.appendChild(f);
                        setTimeout(() => { try { f.remove(); } catch { /* noop */ } }, 60000);
                    } catch { window.open(purl, "_blank", "noopener"); }
                }, 450);
            }
        });
        return () => { if (urlRef.current) window.URL.revokeObjectURL(urlRef.current); };
    }, []);

    // Schließen mit ESC
    useEffect(() => {
        if (!state) return undefined;
        const onKey = (e) => { if (e.key === "Escape") { if (sendOpen) setSendOpen(false); else close(); } };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [state, sendOpen, close]);

    // Aug-2026 (User-Bug „E-Mail-Text nicht änderbar"): Wird das PDF aus einem
    // offenen Radix-Dialog (z. B. Kaufvertrag-Modal) geöffnet, klaut dessen
    // Fokus-Falle jedem Eingabefeld dieser Vorschau sofort den Fokus — Tippen
    // unmöglich. Wir stoppen focusin-Events aus der Vorschau in der Capture-
    // Phase, bevor die Dialog-Fokus-Falle sie sieht.
    useEffect(() => {
        if (!state) return undefined;
        const stopFocusSteal = (e) => {
            const overlay = document.querySelector('[data-testid="pdf-preview-modal"]');
            if (overlay && overlay.contains(e.target)) e.stopImmediatePropagation();
        };
        document.addEventListener("focusin", stopFocusSteal, true);
        return () => document.removeEventListener("focusin", stopFocusSteal, true);
    }, [state]);

    if (!state) return null;

    const download = () => {
        const a = document.createElement("a");
        a.href = state.url;
        a.download = state.filename || "dokument.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const openInNewTab = () => {
        // Zuverlässige Anzeige im nativen PDF-Viewer des Browsers — auch wenn das
        // eingebettete iframe (blob:) leer bleibt (z. B. „PDFs herunterladen"-Einstellung).
        const w = window.open(state.url, "_blank", "noopener");
        if (!w) toast.error("Bitte Popups erlauben, um das PDF zu öffnen.");
    };

    const doPrint = () => {
        // Verstecktes iframe mit dem Blob-PDF erzeugen und daraus drucken.
        // Fällt bei Problemen auf „neuer Tab" zurück (dort native Druckfunktion).
        try {
            const f = document.createElement("iframe");
            f.style.position = "fixed";
            f.style.right = "0"; f.style.bottom = "0";
            f.style.width = "0"; f.style.height = "0"; f.style.border = "0";
            f.src = state.url;
            f.onload = () => {
                try { f.contentWindow.focus(); f.contentWindow.print(); }
                catch { const w = window.open(state.url, "_blank"); if (!w) toast.error("Bitte Popups erlauben, um das PDF zu drucken."); }
            };
            document.body.appendChild(f);
            setTimeout(() => { try { f.remove(); } catch { /* noop */ } }, 60000);
        } catch {
            const w = window.open(state.url, "_blank");
            if (!w) toast.error("Bitte Popups erlauben, um das PDF zu drucken.");
        }
    };

    const switchChannel = (key) => {
        setChannel(key);
        const r = state.recipient || {};
        setToValue(key === "email" ? (r.email || "") : (r.phone || ""));
    };

    const doSend = async () => {
        const value = (toValue || "").trim();
        if (!value) {
            toast.error(channel === "email" ? "Bitte E-Mail-Adresse eingeben." : "Bitte Telefonnummer eingeben.");
            return;
        }
        setSending(true);
        try {
            const fd = new FormData();
            fd.append("file", blobRef.current, state.filename || "dokument.pdf");
            fd.append("channel", channel);
            fd.append("recipient", value);
            fd.append("filename", state.filename || "dokument.pdf");
            fd.append("message", message || "");
            fd.append("doc_label", state.filename || "Dokument");
            fd.append("base_url", process.env.REACT_APP_BACKEND_URL || "");
            const { data } = await api.post("/documents/send", fd);
            if (channel === "whatsapp" && data.wa_url) {
                window.open(data.wa_url, "_blank", "noopener");
                toast.success("WhatsApp geöffnet — Nachricht mit Download-Link bereit zum Senden.");
            } else if (data.queued_approval) {
                toast.info("E-Mail liegt im Postausgang zur Freigabe — sie wird nach Freigabe durch Hauptsitz/Filialleiter versendet.", {
                    duration: 12000,
                    action: {
                        label: "Zum Postausgang",
                        onClick: () => { window.location.href = "/dealer/email-freigabe"; },
                    },
                });
            } else if (data.deduped) {
                toast.info("Identische E-Mail wurde soeben bereits erzeugt — kein erneuter Versand nötig.");
            } else if (data.mocked) {
                toast.success(
                    channel === "email"
                        ? "E-Mail vorbereitet (DEMO — kein echter Versand, API-Key fehlt)."
                        : "SMS vorbereitet (DEMO — kein echter Versand, API-Key fehlt).",
                    { description: data.link },
                );
            } else {
                toast.success(channel === "email" ? "E-Mail versendet." : "SMS versendet.");
            }
            setSendOpen(false);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Versand fehlgeschlagen.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] bg-black/70 flex flex-col" style={{ pointerEvents: "auto" }} data-testid="pdf-preview-modal" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
            <div className="flex items-center justify-between gap-2 flex-wrap px-3 sm:px-4 py-2.5 bg-[#111827] text-white shrink-0 relative">
                <span className="text-sm font-bold truncate inline-flex items-center gap-2 min-w-0 max-w-full" data-testid="pdf-preview-filename">
                    <FileText className="w-4 h-4 text-[#FFCC00] shrink-0" /> <span className="truncate">{state.filename}</span>
                </span>
                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 flex-wrap">
                    <button type="button" onClick={() => setSendOpen((v) => !v)} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2.5 sm:px-3 py-1.5 rounded-sm border border-[#FFCC00]/60 text-[#FFCC00] hover:bg-[#FFCC00]/10 transition-colors" data-testid="pdf-preview-send">
                        <Send className="w-4 h-4" /> <span className="hidden sm:inline">Senden</span>
                    </button>
                    <button type="button" onClick={openInNewTab} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2.5 sm:px-3 py-1.5 rounded-sm border border-white/30 hover:bg-white/10 transition-colors" data-testid="pdf-preview-newtab">
                        <ExternalLink className="w-4 h-4" /> <span className="hidden sm:inline">Neuer Tab</span>
                    </button>
                    <button type="button" onClick={doPrint} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2.5 sm:px-3 py-1.5 rounded-sm border border-white/30 hover:bg-white/10 transition-colors" data-testid="pdf-preview-print">
                        <Printer className="w-4 h-4" /> <span className="hidden sm:inline">Drucken</span>
                    </button>
                    <button type="button" onClick={download} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2.5 sm:px-3 py-1.5 rounded-sm bg-[#FFCC00] text-[#111827] hover:bg-[#e6b800] transition-colors" data-testid="pdf-preview-download">
                        <Download className="w-4 h-4" /> <span className="hidden sm:inline">Herunterladen</span>
                    </button>
                    <button type="button" onClick={close} className="p-1.5 rounded-sm hover:bg-white/10 transition-colors" data-testid="pdf-preview-close" aria-label="Schließen">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {sendOpen && (
                    <div className="absolute right-3 top-full mt-2 w-[360px] max-w-[92vw] bg-white text-[#111827] rounded-md shadow-2xl border border-gray-200 p-4 z-[210]" data-testid="pdf-send-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="text-sm font-bold mb-3">Dokument senden</div>
                        <div className="grid grid-cols-3 gap-1.5 mb-3">
                            {CHANNELS.map((c) => {
                                const Icon = c.icon;
                                const active = channel === c.key;
                                return (
                                    <button key={c.key} type="button" onClick={() => switchChannel(c.key)}
                                        className={`flex flex-col items-center gap-1 py-2 rounded-sm border text-xs font-semibold transition-colors ${active ? "border-[#FFCC00] bg-[#FFFBEB] text-[#111827]" : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}
                                        data-testid={`pdf-send-channel-${c.key}`}>
                                        <Icon className="w-4 h-4" /> {c.label}
                                    </button>
                                );
                            })}
                        </div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">
                            {channel === "email" ? "E-Mail-Adresse" : "Telefonnummer"}
                        </label>
                        <input
                            type={channel === "email" ? "email" : "tel"}
                            value={toValue}
                            onChange={(e) => setToValue(e.target.value)}
                            placeholder={channel === "email" ? "kunde@example.de" : "+49 170 1234567"}
                            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#FFCC00]"
                            data-testid="pdf-send-recipient"
                        />
                        <label className="block text-xs font-semibold text-gray-500 mb-1">
                            Nachricht — Vorschlag anpassbar, einfach ergänzen oder ändern
                        </label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={5}
                            placeholder="Persönliche Notiz an den Kunden…"
                            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm mb-2 resize-y focus:outline-none focus:ring-2 focus:ring-[#FFCC00]"
                            data-testid="pdf-send-message"
                        />
                        {channel !== "email" && (
                            <p className="text-[11px] text-gray-400 mb-3 leading-snug">
                                {channel === "whatsapp"
                                    ? "WhatsApp kann keine Datei anhängen — es wird ein sicherer Download-Link gesendet."
                                    : "SMS kann keine Datei anhängen — es wird ein sicherer Download-Link gesendet."}
                            </p>
                        )}
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setSendOpen(false)} className="text-xs font-semibold px-3 py-2 rounded-sm text-gray-500 hover:bg-gray-100" data-testid="pdf-send-cancel">
                                Abbrechen
                            </button>
                            <button type="button" onClick={doSend} disabled={sending} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black disabled:opacity-60 transition-colors" data-testid="pdf-send-submit">
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Senden
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <PdfCanvasViewer blob={state.blob} />
        </div>
    );
}
